/**
 * useAdminSession — centralizes the shared-secret admin auth pattern.
 * Stores the password in sessionStorage; clears it automatically on 401.
 * Used by /admin/submissions and /admin/blog.
 */
import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HTTP } from '../lib/httpStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PASSWORD_STORAGE_KEY = 'ban_admin_pw';

export const useAdminSession = () => {
  const [password, setPassword] = useState(
    () => sessionStorage.getItem(PASSWORD_STORAGE_KEY) || ''
  );

  const login = useCallback(async (pw) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/verify`,
        {},
        { headers: { 'X-Admin-Password': pw } }
      );
      sessionStorage.setItem(PASSWORD_STORAGE_KEY, pw);
      setPassword(pw);
      return true;
    } catch (error) {
      toast.error(
        error.response?.status === HTTP.UNAUTHORIZED ? 'Incorrect password' : 'Login failed'
      );
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(PASSWORD_STORAGE_KEY);
    setPassword('');
  }, []);

  const handle401 = useCallback(() => {
    sessionStorage.removeItem(PASSWORD_STORAGE_KEY);
    setPassword('');
    toast.error('Session expired', { description: 'Please sign in again.' });
  }, []);

  const authedFetch = useCallback(
    async (config) => {
      try {
        return await axios({
          ...config,
          headers: { ...(config.headers || {}), 'X-Admin-Password': password },
        });
      } catch (error) {
        if (error.response?.status === HTTP.UNAUTHORIZED) {
          handle401();
        }
        throw error;
      }
    },
    [password, handle401]
  );

  return { password, login, logout, authedFetch, isLoggedIn: !!password };
};
